import { forwardRef, type SVGProps } from "react";

const DEFAULT_COMPLETE_THRESHOLD = 75;

interface CircleProgressProps extends SVGProps<SVGSVGElement> {
  progress: number;
  minProgressFillPercent?: number;
  maxProgressFillPercent?: number;
  completeThreshold?: number;
}

export const CircleProgress = forwardRef<SVGSVGElement, CircleProgressProps>(
  (
    {
      progress,
      minProgressFillPercent,
      maxProgressFillPercent,
      completeThreshold = DEFAULT_COMPLETE_THRESHOLD,
      ...props
    },
    ref,
  ) => {
    if (progress > completeThreshold) {
      return <CompletedIcon ref={ref} aria-label="Completed" {...props} />;
    }
    return (
      <InProgressIcon
        ref={ref}
        progress={progress}
        minProgressFillPercent={minProgressFillPercent}
        maxProgressFillPercent={maxProgressFillPercent}
        aria-label={`In Progress, ${Math.round(progress)} percent`}
        {...props}
      />
    );
  },
);

interface InProgressIconProps extends React.SVGProps<SVGSVGElement> {
  progress?: number;
  minProgressFillPercent?: number;
  maxProgressFillPercent?: number;
}

CircleProgress.displayName = "CircleProgress";

const InProgressIcon = forwardRef<SVGSVGElement, InProgressIconProps>(
  (
    {
      progress = 0,
      minProgressFillPercent = 0,
      maxProgressFillPercent = 75,
      ...props
    },
    ref,
  ) => {
    const radius = 8;
    const circumference = 2 * Math.PI * radius;

    const fillLength =
      ((minProgressFillPercent +
        (progress / 100) * (maxProgressFillPercent - minProgressFillPercent)) /
        100) *
      circumference;

    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        ref={ref}
        role="img"
        {...props}
      >
        <title>In Progress</title>
        <path
          d="M10 2C11.0506 2 12.0909 2.20693 13.0615 2.60896C14.0321 3.011 14.914 3.60028 15.6569 4.34315C16.3997 5.08602 16.989 5.96793 17.391 6.93853C17.7931 7.90914 18 8.94943 18 10C18 11.0506 17.7931 12.0909 17.391 13.0615C16.989 14.0321 16.3997 14.914 15.6569 15.6569C14.914 16.3997 14.0321 16.989 13.0615 17.391C12.0909 17.7931 11.0506 18 10 18C8.94942 18 7.90913 17.7931 6.93853 17.391C5.96793 16.989 5.08601 16.3997 4.34314 15.6569C3.60027 14.914 3.011 14.0321 2.60896 13.0615C2.20693 12.0909 2 11.0506 2 10C2 8.94942 2.20693 7.90913 2.60897 6.93853C3.011 5.96792 3.60028 5.08601 4.34315 4.34314C5.08602 3.60027 5.96793 3.011 6.93854 2.60896C7.90914 2.20692 8.94943 2 10 2L10 2Z"
          stroke="#696969"
          strokeOpacity="0.933333"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${fillLength} ${circumference}`}
          style={{
            transition: "stroke-dasharray 0.3s ease-in-out",
            transform: "rotate(-45deg)",
            transformOrigin: "center",
          }}
        />
      </svg>
    );
  },
);

InProgressIcon.displayName = "InProgressIcon";

const CompletedIcon = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        ref={ref}
        role="img"
        {...props}
      >
        <title>Completed</title>
        <path
          d="M9.99902 1C14.9691 1 18.9988 5.02901 18.999 9.99902C18.999 14.9693 14.9693 18.999 9.99902 18.999C5.02901 18.9988 1 14.9691 1 9.99902C1.00026 5.02917 5.02917 1.00026 9.99902 1ZM14.7197 7.30566C14.3362 6.90859 13.7029 6.89702 13.3057 7.28027L8.82324 11.6084L6.69434 9.55371C6.29709 9.17012 5.66394 9.18092 5.28027 9.57812C4.89692 9.97538 4.90855 10.6086 5.30566 10.9922L8.12891 13.7197C8.51641 14.0937 9.13111 14.0938 9.51855 13.7197L14.6943 8.71973C15.0914 8.33617 15.103 7.70293 14.7197 7.30566Z"
          fill="#3B82F6"
        />
      </svg>
    );
  },
);
CompletedIcon.displayName = "CompletedIcon";
